import { devLog } from '../../utils/devLog';

import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../hooks/useAppContext';
import { useToast } from '../common/Toast';
import { saveInterviewLink, getCandidateProfile, getCandidateScreenings, sendInterviewEmail, getRoleQuestions } from '../../utils/api';
import EmptyState from '../common/EmptyState';
import InterviewTypeOption from './InterviewTypeOption';
import Modal from '../common/Modal';

const InterviewPanel = ({ applicant, jobId, jobMongoId, onInterviewComplete, onLinkGenerated }) => {
  const { jobs } = useAppContext();
  const { showSuccess, showError, showInfo } = useToast();
  const [selectedType, setSelectedType] = useState(null);
  const [generatedLink, setGeneratedLink] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [existingLink, setExistingLink] = useState(null);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [pendingType, setPendingType] = useState(null);
  const [interviewMode, setInterviewMode] = useState(null);
  const [duration, setDuration] = useState('');
  const [roleQuestions, setRoleQuestions] = useState([]);

  const selectedJob = jobs.find(job => job.id === jobId);
  const roleId = selectedJob?.roleId;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!applicant || !applicant.email || !roleId) {
        setCandidateProfile(null);
        setExistingLink(null);
        setGeneratedLink('');
        setSelectedType(null);
        return;
      }

      setSelectedType(null);
      setGeneratedLink('');
      setExistingLink(null);

      setIsLoadingProfile(true);
      try {
        const result = await getCandidateProfile(roleId, applicant.email);
        if (result.success) {
          setCandidateProfile(result.data);
        } else {
          setCandidateProfile(applicant);
        }

        const screeningsResult = await getCandidateScreenings(roleId, applicant.email);
        if (screeningsResult.success && screeningsResult.data && screeningsResult.data.length > 0) {
          // Find if any screening has an interview link
          const linkScreening = screeningsResult.data.find(s => s.interviewLink);
          const dbLink = linkScreening?.interviewLink;
          
          const candidateId = applicant?.candidateId || result.data?.candidateId || applicant?.email;

          let storedLink = null;
          let storedType = null;

          // Check localStorage first (for current session/immediate feedback)
          for (const type of ['call', 'chat']) {
            const storageKey = `interview_link_${roleId}_${candidateId}_${type}`;
            const link = localStorage.getItem(storageKey);
            if (link) {
              storedLink = link;
              storedType = type;
              break;
            }
          }

          // Prefer database link if available
          if (dbLink) {
            setGeneratedLink(dbLink);
            // Try to determine type from URL if possible
            setSelectedType(dbLink.includes('/chat/') ? 'chat' : 'call');
          } else if (storedLink) {
            setGeneratedLink(storedLink);
            setSelectedType(storedType);
          }
        }
      } catch (err) {
        devLog.warn('Could not fetch detailed profile, using applicant data:', err);
        setCandidateProfile(applicant);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [applicant, roleId]);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!roleId) return;
      try {
        const result = await getRoleQuestions(roleId);
        if (result.success) {
          setRoleQuestions(result.data.questions || []);
        }
      } catch (err) {
        setRoleQuestions([]);
      }
    };
    fetchQuestions();
  }, [roleId]);

  const checkExistingLink = async (type) => {
    if (!applicant?.email || !roleId) return null;

    try {
      const screeningsResult = await getCandidateScreenings(roleId, applicant.email);
      if (screeningsResult.success && screeningsResult.data && screeningsResult.data.length > 0) {
        const matchingScreening = screeningsResult.data.find(
          screening =>
            (screening.interviewType === type || screening.type === type) ||
            (screening.interviewLink && screening.interviewLink.includes(`/${type}/`))
        );

        if (matchingScreening?.interviewLink) {
          // Sync to localStorage if found in DB but missing locally
          const candidateId = applicant?.candidateId || candidateProfile?.candidateId || applicant?.email;
          const storageKey = `interview_link_${roleId}_${candidateId}_${type}`;
          localStorage.setItem(storageKey, matchingScreening.interviewLink);
          return matchingScreening.interviewLink;
        }
      }
    } catch (err) {
      devLog.warn('Error checking for existing link:', err);
    }

    const candidateId = applicant?.candidateId || candidateProfile?.candidateId || applicant?.email;
    const storageKey = `interview_link_${roleId}_${candidateId}_${type}`;
    const storedLink = localStorage.getItem(storageKey);

    if (storedLink) {
      return storedLink;
    }

    if (generatedLink && generatedLink.includes(`/${type}/`)) {
      return generatedLink;
    }

    return null;
  };

  const handleTypeSelect = async (type) => {
    const existing = await checkExistingLink(type);

    if (existing) {
      setPendingType(type);
      setExistingLink(existing);
      setShowRegenerateModal(true);
    } else {
      setSelectedType(type);
      setExistingLink(null);
      setPendingType(null);

      if (roleId) {
        const candidateId = applicant?.candidateId || candidateProfile?.candidateId || applicant?.email;
        const storageKey = `interview_link_${roleId}_${candidateId}_${type}`;
        const storedLink = localStorage.getItem(storageKey);
        if (storedLink) {
          setGeneratedLink(storedLink);
        } else {
          setGeneratedLink('');
        }
      }
    }
  };

  const handleRegenerateConfirm = () => {
    setGeneratedLink('');
    setSelectedType(null);
    setExistingLink(null);
    setShowRegenerateModal(false);
    setPendingType(null);

    if (roleId && applicant?.email) {
      const candidateId = applicant?.candidateId || candidateProfile?.candidateId || applicant?.email;
      const storageKey = `interview_link_${roleId}_${candidateId}_${pendingType}`;
      localStorage.removeItem(storageKey);
    }
  };

  const displayData = candidateProfile || applicant;

  if (!applicant) {
    return (
      <EmptyState
        message="Choose an applicant to generate interview link"
        icon="👤"
      />
    );
  }

  const handleGenerateLink = async () => {
    if (!selectedType) {
      showError('Please select an interview type!');
      return;
    }

    if (!interviewMode) {
      showError('Please select an interview mode (HR Questions or LLM Based)!');
      return;
    }

    if (!duration || isNaN(duration) || parseInt(duration) < 1) {
      showError('Please enter a valid interview duration in minutes!');
      return;
    }

    if (interviewMode === 'hr' && roleQuestions.length < 5) {
      showError(`HR Interview mode requires at least 5 questions. This role only has ${roleQuestions.length}. Please add more questions in the Interview Questions tab.`);
      return;
    }

    if (!applicant?.email) {
      showError('Applicant email is required to generate interview link');
      return;
    }

    if (!roleId) {
      showError('Role ID is missing. Please select a valid job position.');
      return;
    }

    setIsGenerating(true);

    try {
      let candidateId = (applicant?.candidateId && applicant.candidateId.trim()) ||
                        (applicant?._id && applicant._id.toString()) ||
                        (candidateProfile?.candidateId && candidateProfile.candidateId.trim()) ||
                        (candidateProfile?._id && candidateProfile._id.toString());

      if (!candidateId || candidateId.trim() === '') {
        try {
          const profileResult = await getCandidateProfile(roleId, applicant.email);
          if (profileResult.success && (profileResult.data?.candidateId || profileResult.data?._id)) {
            candidateId = (profileResult.data.candidateId || profileResult.data._id).toString().trim();
            setCandidateProfile(profileResult.data);
          }
        } catch (err) {
          devLog.warn('Could not fetch candidate ID, using email as fallback:', err);
        }
      }

      const linkId = Math.random().toString(36).substring(2, 15);
      const finalRoleId = jobMongoId || roleId;
      const interviewBaseUrl = process.env.REACT_APP_INTERVIEW_URL || 'http://localhost:3000';
      const link = `${interviewBaseUrl}/${selectedType}/${linkId}?candidateId=${encodeURIComponent(candidateId || '')}&roleId=${encodeURIComponent(finalRoleId)}&mode=${interviewMode}&duration=${duration}`;

      const result = await saveInterviewLink(roleId, applicant.email, link, selectedType, linkId);

      if (result.success) {
        setGeneratedLink(link);

        if (roleId) {
          const finalCandidateId = candidateId || applicant?.email;
          const storageKey = `interview_link_${roleId}_${finalCandidateId}_${selectedType}`;
          localStorage.setItem(storageKey, link);
        }

        showSuccess('🔗 Interview link generated and saved successfully!');

        if (onLinkGenerated) {
          onLinkGenerated();
        }
      } else {
        throw new Error(result.error || 'Failed to save interview link');
      }
    } catch (error) {
      devLog.error('Error generating interview link:', error);
      showError(`Failed to generate interview link: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(generatedLink)
          .then(() => showSuccess('✔ Link copied to clipboard!'))
          .catch(() => showError('Failed to copy link to clipboard'));
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = generatedLink;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        showSuccess('✔ Link copied to clipboard!');
      }
    } catch (err) {
      showError('Failed to copy link to clipboard');
    }
  };

  const handleSendEmail = async () => {
    if (!generatedLink) {
      showError('Please generate an interview link first');
      return;
    }

    if (!selectedType) {
      showError('Interview type is not selected');
      return;
    }

    const job = jobs.find(j => j.id === jobId);
    const candidateName = displayData?.name || applicant?.name || 'Candidate';
    const candidateEmail = displayData?.email || applicant?.email;
    const jobTitle = job?.title || 'Position';

    if (!candidateEmail) {
      showError('Candidate email is not available');
      return;
    }

    if (!roleId) {
      showError('Role ID is missing. Please select a valid job position.');
      return;
    }

    try {
      const result = await sendInterviewEmail(
        roleId,
        candidateEmail,
        generatedLink,
        selectedType,
        candidateName,
        jobTitle
      );

      if (result.success) {
        showSuccess('📧 Interview email sent successfully to candidate!', 5000);
      } else {
        showError(result.error || 'Failed to send email. Please check SMTP configuration.', 6000);
      }
    } catch (error) {
      devLog.error('Error sending email:', error);
      showError(`Failed to send email: ${error.message || 'Please try again.'}`, 6000);
    }
  };

  const handleSendWhatsApp = () => {
    const job = jobs.find(j => j.id === jobId);
    const candidateName = displayData?.name || 'Candidate';
    const phone = (displayData?.phone || applicant?.phone || '').replace(/[^0-9]/g, '');

    if (!phone) {
      showError('Candidate phone number is not available');
      return;
    }

    const message = `Hi ${candidateName}, we would like to invite you for an AI-powered interview for the ${job?.title} position. Please click: ${generatedLink}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`);
    showInfo('📱 Opening WhatsApp...');
  };

  return (
    <div>
      <div style={{
        background: 'linear-gradient(135deg, rgba(72, 187, 120, 0.1), rgba(56, 161, 105, 0.05))',
        padding: '20px',
        borderRadius: '15px',
        border: '2px solid rgba(72, 187, 120, 0.3)',
        marginBottom: '20px'
      }}>
        <h4 style={{ fontSize: '1.1rem', color: '#2d3748', marginBottom: '15px' }}>
          Selected Candidate
        </h4>
        {isLoadingProfile ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#718096' }}>
            Loading candidate details...
          </div>
        ) : (
          <>
            <div style={{ padding: '8px 0', color: '#4a5568', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <strong>Name:</strong> {displayData?.name || 'Unknown'}
            </div>
            <div style={{ padding: '8px 0', color: '#4a5568', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <strong>Email:</strong> {displayData?.email || 'Not provided'}
            </div>
            <div style={{ padding: '8px 0', color: '#4a5568', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <strong>Phone:</strong> {displayData?.phone || 'Not provided'}
            </div>
            <div style={{ padding: '8px 0', color: '#4a5568', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <strong>Experience:</strong> {displayData?.experience || 'Not specified'}
            </div>
            <div style={{ padding: '8px 0', color: '#4a5568', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <strong>Education:</strong> {displayData?.education || 'Not specified'}
            </div>
            <div style={{ padding: '8px 0', color: '#4a5568' }}>
              <strong>Location:</strong> {displayData?.location && displayData.location !== 'N/A' ? displayData.location : 'Not specified'}
            </div>
            {displayData?.cvScore && (
              <div style={{
                padding: '8px 0',
                color: '#4a5568',
                marginTop: '10px',
                paddingTop: '15px',
                borderTop: '1px solid rgba(0,0,0,0.05)'
              }}>
                <strong>CV Score:</strong> <span style={{
                  color: '#48bb78',
                  fontWeight: '700',
                  fontSize: '1.1rem'
                }}>{displayData.cvScore}</span>
              </div>
            )}
          </>
        )}
      </div>

      <div style={{
        background: 'white',
        padding: '25px',
        borderRadius: '15px',
        border: '2px solid #e2e8f0'
      }}>
        {!generatedLink ? (
          <>
            <h4 style={{ color: '#4a5568', marginBottom: '15px', fontSize: '1.1rem' }}>
              Interview Type
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <InterviewTypeOption
                type={{ id: 'call', label: 'Voice Call', description: 'AI-conducted phone interview' }}
                isSelected={selectedType === 'call'}
                onSelect={() => handleTypeSelect('call')}
              />
              <InterviewTypeOption
                type={{ id: 'chat', label: 'Text Chat', description: 'AI-conducted text interview' }}
                isSelected={selectedType === 'chat'}
                onSelect={() => handleTypeSelect('chat')}
              />
            </div>

            {selectedType && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontWeight: 600, color: '#2d3748', marginBottom: '10px', display: 'block' }}>
                  Interview Mode
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div
                    onClick={() => setInterviewMode('hr')}
                    style={{
                      padding: '16px', borderRadius: '10px', cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${interviewMode === 'hr' ? '#667eea' : '#e2e8f0'}`,
                      background: interviewMode === 'hr' ? '#eef2ff' : 'white'
                    }}
                  >
                    <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>📋</div>
                    <div style={{ fontWeight: 700, color: '#2d3748', fontSize: '0.9rem' }}>HR Questions</div>
                    <div style={{ fontSize: '0.75rem', color: '#718096', marginTop: '4px' }}>Uses questions you added manually</div>
                    {interviewMode === 'hr' && roleQuestions.length < 5 && (
                      <div style={{ marginTop: '8px', color: '#dc2626', fontSize: '0.75rem', fontWeight: 600 }}>
                        ⚠️ Only {roleQuestions.length}/5 questions added
                      </div>
                    )}
                    {interviewMode === 'hr' && roleQuestions.length >= 5 && (
                      <div style={{ marginTop: '8px', color: '#065f46', fontSize: '0.75rem', fontWeight: 600 }}>
                        ✅ {roleQuestions.length} questions ready
                      </div>
                    )}
                  </div>
                  <div
                    onClick={() => setInterviewMode('llm')}
                    style={{
                      padding: '16px', borderRadius: '10px', cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${interviewMode === 'llm' ? '#667eea' : '#e2e8f0'}`,
                      background: interviewMode === 'llm' ? '#eef2ff' : 'white'
                    }}
                  >
                    <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>🤖</div>
                    <div style={{ fontWeight: 700, color: '#2d3748', fontSize: '0.9rem' }}>LLM Based</div>
                    <div style={{ fontSize: '0.75rem', color: '#718096', marginTop: '4px' }}>AI generates questions from JD & CV</div>
                  </div>
                </div>

                {interviewMode && (
                  <div>
                    <label style={{ fontWeight: 600, color: '#2d3748', marginBottom: '8px', display: 'block' }}>
                      ⏱️ Interview Duration
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="number"
                        min="5"
                        max="120"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        placeholder="e.g. 30"
                        style={{
                          width: '100px', padding: '10px', borderRadius: '8px',
                          border: '2px solid #667eea', fontSize: '1rem',
                          fontWeight: 600, color: '#2d3748', outline: 'none'
                        }}
                      />
                      <span style={{ color: '#718096', fontSize: '0.85rem' }}>minutes</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={handleGenerateLink}
              disabled={isGenerating || !selectedType || !interviewMode || !duration}
              className="button button-primary"
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px'
              }}
            >
              {isGenerating ? 'Generating...' : '🔗 Generate Interview Link'}
            </button>
          </>
        ) : (
          <>
            <div style={{
              background: '#f0f9ff',
              border: '2px solid #bae6fd',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#0369a1',
                fontWeight: 600,
                marginBottom: '15px'
              }}>
                🔗 Interview Link Generated ({selectedType === 'call' ? 'Voice Call' : 'Text Chat'})
              </div>

              {generatedLink && (
                <>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                    <input
                      type="text"
                      value={generatedLink}
                      readOnly
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: '2px solid #bae6fd',
                        borderRadius: '8px',
                        background: 'white',
                        fontSize: '0.9rem'
                      }}
                    />
                    <button
                      onClick={handleCopyLink}
                      style={{
                        background: '#0369a1',
                        color: 'white',
                        border: 'none',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      📋 Copy
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={handleSendEmail}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: '#3b82f6',
                        color: 'white'
                      }}
                    >
                      📧 Send Email
                    </button>
                    <button
                      onClick={handleSendWhatsApp}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: '#10b981',
                        color: 'white'
                      }}
                    >
                      📱 Send WhatsApp
                    </button>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => {
                setPendingType(selectedType);
                setExistingLink(generatedLink);
                setShowRegenerateModal(true);
              }}
              className="button"
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                padding: '12px',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              🔄 Regenerate Interview Link
            </button>
          </>
        )}
      </div>

      <Modal
        isOpen={showRegenerateModal}
        onClose={() => {
          setShowRegenerateModal(false);
          setPendingType(null);
          setExistingLink(null);
        }}
        onConfirm={handleRegenerateConfirm}
        title="🔄 Regenerate Interview Link?"
        message={
          <div>
            <p style={{ marginBottom: '15px', lineHeight: 1.6 }}>
              An interview link for <strong>{pendingType === 'call' ? 'Voice Call' : 'Text Chat'}</strong> has already been generated for this candidate.
            </p>
            {existingLink && (
              <div style={{
                background: '#f7fafc',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '15px',
                borderLeft: '4px solid #667eea',
                wordBreak: 'break-all'
              }}>
                <strong style={{ color: '#2d3748', display: 'block', marginBottom: '8px' }}>
                  Existing Link:
                </strong>
                <p style={{ color: '#4a5568', margin: 0, fontSize: '0.9rem' }}>
                  {existingLink}
                </p>
              </div>
            )}
            <p style={{ color: '#4a5568', margin: 0, lineHeight: 1.6 }}>
              Do you want to generate a new link? The old link will be replaced.
            </p>
          </div>
        }
        confirmText="Yes, Regenerate"
        cancelText="Cancel"
        type="primary"
      />
    </div>
  );
};

export default InterviewPanel;
